// src/PO-Process/PODetails.jsx

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button
} from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../axiosConfig";
import LoadingSpinner from "../components/LoadingSpinner";
import { Toaster } from "../components/Toaster";
import ContainedButton from "../components/ui/ContainedButton";
import { getCustomers, getItemNames, getPOExtractedInfo, getSkuMaster, updatePOData, searchOutletCode } from "./api/api";
import CustomTabs from "./components/CustomTabs";
import EditLabelModal from "./components/EditLabelModel";
import OutletCodeCell from "./components/OutletCodeCell";
import ItemNameEditor from "./components/ItemNameEditor";
import SDCodeEditor from "./components/SDCodeEditor";
import { getUser } from "../helpers";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const PODetails = ({ open }) => {
  const { filename } = useParams();
  const navigate = useNavigate();
  const [poData, setPoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [itemNames, setItemNames] = useState([]);
  const [labelSynonyms, setLabelSynonyms] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [currentEditLabel, setCurrentEditLabel] = useState("");
  const [newSynonym, setNewSynonym] = useState("");
  const [skuMaster, setSkuMaster] = useState([]);
  const [excelData, setExcelData] = useState(null);
  const [excelHtml, setExcelHtml] = useState("");
  const user = getUser(); 

  const handleBackClick = () => {
    navigate("/po-home");
  };

  const isExcelFile = (filename) => {
    const ext = filename?.split(".").pop()?.toLowerCase();
    return ["xlsx", "xls", "csv"].includes(ext);
  };

  const isImageFile = (filename) => {
    const ext = filename?.split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png"].includes(ext);
  };

  const isJSONfile = (filename) => {
    const ext = filename?.split(".").pop()?.toLowerCase();
    return ["json"].includes(ext);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [data, customerList, itemNameList, skuMasterList] = await Promise.all([
          getPOExtractedInfo(filename),
          getCustomers(),
          getItemNames(),
          getSkuMaster(), // <-- Fetch SKU master
        ]);
        setPoData(data);
        setCustomers(customerList);
        setItemNames(itemNameList);
        setSkuMaster(skuMasterList); // <-- Store it
        if (data?.Customer && filename && !isJSONfile(filename)) {
          await copyFileToUploads(data.Customer); 
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        Toaster.error("Error fetching data:", error)
      } finally {
        setLoading(false);
      }
      if (isExcelFile(filename)) {
        try {
          const res = await api.get(`/uploads/${filename}`, {
            responseType: "arraybuffer",
          });

          const workbook = XLSX.read(res.data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          // --- FIX: Determine max number of columns ---
          const maxCols = Math.max(...jsonData.map(r => r.length));

          // --- FIX: Pad rows to rectangular table ---
          const normalized = jsonData.map(row => {
            const newRow = [...row];
            while (newRow.length < maxCols) newRow.push("");
            return newRow;
          });

          setExcelData(normalized);
        } catch (err) {
          console.error("Failed to load Excel:", err);
        }
        try {
          const res = await api.get(`/excel-html?filename=${filename}`);
          setExcelHtml(res.data.html);
        } catch(err) {
          console.error("excel-html failed:", err);
        }
      }
      if (isJSONfile(filename)) {
        try {
          const res = await api.get(`/excel-html?filename=${filename}`);
          setExcelHtml(res.data.html);
        } catch (err) {
          console.error("excel-html failed:", err);
        }
      }
    };
    if (filename) {
      fetchData();
      console.log("FILENAME:", filename, "ISEXCEL?", isExcelFile(filename));
    }
  }, [filename]);

  const getMissingFields = useCallback(() => {
    if (!poData) return {};

    const missing = {};
    const getRootText = (label) => poData?.[label]?.trim?.();

    if (!getRootText("Outlet Code")) missing["Outlet Code"] = true;

    return missing;
  }, [poData]);

  const isApproveDisabled = useMemo(() => {
    if (!poData) return true;

    const getRootText = (label) => poData?.[label]?.trim?.();

    const outletCode = getRootText("Outlet Code");

    const hasInvalidItemCode = (poData["Table Body"] || []).some(row => {
      const code = (row["SD_Code"] || "").toString().trim();
      return !code || code.toLowerCase() === "null" || code.toLowerCase() === "undefined";
    });

    // Check missing Item Name
    const hasMissingItemName = (poData["Table Body"] || []).some(row => {
      const name = (row["Item Name"] || "").toString().trim();
      return !name || name.toLowerCase() === "null" || name.toLowerCase() === "undefined";
    });

    const warningMessages = [];

    if (!outletCode) warningMessages.push("Outlet Code is missing.");
    if (hasInvalidItemCode) warningMessages.push("Missing/Invalid item codes.");
    if (hasMissingItemName) warningMessages.push("Some item names are missing.");

    if (warningMessages.length > 0) {
      // Show warning messages (you can log or display them in your UI)
      console.warn("Warnings:", warningMessages.join(" "));
      Toaster.warning("Warnings:", warningMessages.join(" "))
    }

    return !outletCode || hasInvalidItemCode;
  }, [poData]);

  const CustomHeader = (props) => {
    const { displayName, column } = props;

    const handleEditLabel = () => {
      setCurrentEditLabel(displayName);
      setNewSynonym("");

      setModalOpen(true);
    };

    return (
      <div
        style={{
          fontWeight: 700,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",   // THIS is key
          paddingRight: 8,
        }}
      >
        <span>{displayName}</span>
        {column.getColId() !== "SD_Code" && (
          <span
            onClick={handleEditLabel}
            style={{ padding: 4, cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <EditIcon
              sx={{
                border: "1px solid #fff",
                borderRadius: "5px",
                padding: "2px",
              }}
              fontSize="small"
            />
          </span>
        )}
      </div>
    );
  };

  const onCellValueChanged = useCallback(async (params) => {

    try {
      const updatedRow = params.data;
      const colId = params.colDef.field;
      const newValue = params.newValue;

      if (!tabValue) {
        // Update backend
        await updatePOData({
          type: "header",
          filename,
          old_label: updatedRow.Label,
          label: updatedRow.Label,
          new_value: updatedRow["Detected Text"],
          user
        });

        // Update local state so Approve button re-evaluates immediately
        setPoData(prevPoData => {
          const newPoData = { ...prevPoData };

          if (updatedRow.Label === "OutletCode") {
            newPoData["Outlet Code"] = updatedRow["Detected Text"];
          }

          newPoData.Header = {
            ...newPoData.Header,
            [updatedRow.Label]: updatedRow["Detected Text"],
          };

          return newPoData;
        });

        // Special case: OutletCode triggers DC Name fetch
        if (updatedRow.Label === "OutletCode" && newValue) {
          try {
            const response = await api.get("/get-dc-name", {
              params: { OutletCode: newValue }
            });

            const dcName = response.data?.[0] || "";

            await updatePOData({
              type: "header",
              filename,
              old_label: "DC Name",
              label: "DC Name",
              new_value: dcName,
              user
            });

            setPoData(prev => ({
              ...prev,
              Header: {
                ...prev.Header,
                ["DC Name"]: dcName,
              }
            }));
          } catch (err) {
            console.error("Failed to fetch DC name:", err);
            Toaster.error("Failed to fetch DC name:", err);
          }
        }
      } else {
        // Items List section
        await updatePOData({
          type: "item",
          filename,
          old_label: colId,
          label: colId,
          new_value: newValue,
          rowIndex: params.node.rowIndex,
          user
        });
      }
    } catch (error) {
      console.error("Error updating PO data:", error);
      Toaster.error("Error updating PO data:", error);
    }
  }, [filename, tabValue]);

  const LabelCellRenderer = (props) => {
    const label = props.value;

    const handleEditLabel = () => {
      setCurrentEditLabel(label);
      setNewSynonym("");

      setModalOpen(true);
    };

    const shouldShowEditButton = label !== "OutletCode" && label !== "Customer" && label !== "Invoice Date";

    return (
      <div
        style={{
          fontWeight: 700,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",   // THIS is key
          paddingRight: 8,
        }}
      >
        <span>{label}</span>
        {shouldShowEditButton && (
          <span
            onClick={handleEditLabel}
            style={{ padding: 4, cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <EditIcon
              sx={{
                border: "1px solid #000",
                borderRadius: "5px",
                padding: "2px",
              }}
              fontSize="small"
            />
          </span>
        )}
      </div>
    );
  };

  const headerSummaryColumns = useMemo(() => [
    {
      field: "Label",
      flex: 1,
      sortable: true,
      filter: true,
      cellRenderer: LabelCellRenderer,
    },
    {
      field: "Detected Text",
      flex: 1,
      editable: (params) => params.data.Label !== "Customer" && params.data.Label !== "OutletCode",
      cellRenderer: (params) => {
        const label = params.data.Label;
        const value = params.value;
        const missingFields = getMissingFields();

        if (label === "Customer") {
          return <span>{value}</span>;
        }
        
        if (label === "OutletCode") {
          return (
            <OutletCodeCell
              value={value}
              filename={filename}
              setPoData={setPoData}
              platformName={poData?.Customer}
            />
          );
        }

        const isRequiredField =
          label === "Invoice Date" ||
          label === "OutletCode";

        return (
          <div>
            <div>
              {value || (isRequiredField && <span style={{ color: "red" }}>*Required Field</span>)}
            </div>
          </div>
        );
      },
      cellStyle: (params) => {
        const label = params.data.Label;

        if (label === "Customer") {
          return { backgroundColor: "#e6f2ff" };
        }

        return {};
      },
    },
  ], [customers, getMissingFields]);

  const itemListColumns = useMemo(() => {
    const fields = [
      { field: "SD_Code", flex: 0.5 },
      { field: "Item Name", flex: 2 },
      { field: "Quantity", flex: 1 },
      { field: "Total", flex: 1 },
    ];

    return fields.map(({ field, flex }) => {
      const columnDef = {
        field,
        flex,
        sortable: true,
        filter: true,
        editable: field !== "SD_Code" && field !== "Item Name",
        headerComponent: CustomHeader,
        headerName: field === "SD_Code" ? "Item Code" : field,
        cellStyle: {},
      };

      if (field === "Item Name") {
        columnDef.editable = true;
        columnDef.cellEditor = ItemNameEditor;
        // columnDef.cellEditorPopup = true;
        columnDef.cellEditorParams = (params) => ({
          ...params,
          rowIndex: params.rowIndex,
          context: params.context,
        });
      }

      if (field === "SD_Code") {
        columnDef.editable = true;
        columnDef.cellEditor = SDCodeEditor;
        columnDef.type = "text";
        columnDef.valueFormatter = (params) => params.value?.toString?.() || "";
        columnDef.cellEditorParams = (params) => ({
          ...params,
          rowIndex: params.rowIndex,
          context: params.context,
        });
      }

      return columnDef;
    });
  }, []);

  const headerSummaryRowData = useMemo(() => {
    if (!poData) return [];

    const getRootText = (label) => poData?.[label]?.trim?.();
    const poType = getRootText("PO Type");

    // Function to exclude Expected Delivery Date when PO Type is not DAILY
    const shouldInclude = (label) => {
      if (label === "Expected Delivery Date" && poType !== "DAILY") return false;
      return true;
    };

    return [
      { Label: "OutletCode", "Detected Text": poData["Outlet Code"] },
      { Label: "Customer", "Detected Text": poData.Customer },
      ...Object.entries(poData.Header || {})
        .filter(([label]) => shouldInclude(label))
        .map(([label, value]) => ({
          Label: label,
          "Detected Text": value,
        })),
      ...Object.entries(poData["Table Summary"] || {})
        .filter(([label]) => shouldInclude(label))
        .map(([label, value]) => ({
          Label: label,
          "Detected Text": value,
        })),
    ];
  }, [poData]);

  // Validate date strictly in YYYY-MM-DD format
  const isValidDate = (dateStr) => {
    if (!dateStr) return false;

    // Must match YYYY-MM-DD exactly
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateStr)) return false;

    // Reject invalid day like 2025-12-00
    const [y, m, d] = dateStr.split("-").map(Number);
    if (d === 0 || m === 0) return false;

    const parsed = dayjs(dateStr, "YYYY-MM-DD", true);
    return parsed.isValid();
  };

  const handleApprovePO = async () => {
    try {
      // Extract header values
      const poDate = poData?.Header?.["PO Date"];
      const expectedDateStr = poData?.Header?.["Expected Delivery Date"];
      const expiryDate = poData?.Header?.["PO Expiry Date"];

      let warnMessages = [];

      // Check PO Date validity
      if (!isValidDate(poDate)) {
        warnMessages.push("• PO Date is missing or invalid");
      }

      // Check Expected Delivery Date validity
      if (!isValidDate(expectedDateStr)) {
        warnMessages.push("• Expected Delivery Date is missing or invalid");
      }

      // Check Expiry Date validity
      if (!isValidDate(expiryDate)) {
        warnMessages.push("• Expiry Date is missing or invalid");
      }

      // Expected delivery date D+2 check (only if valid)
      if (isValidDate(expectedDateStr)) {
        const expectedDate = dayjs(expectedDateStr).startOf("day");
        const todayPlus2 = dayjs().add(2, "day").startOf("day");

        if (!expectedDate.isSame(todayPlus2, "day")) {
          warnMessages.push("• This PO is not due for verification today (Expected delivery date ≠ Today+2)");
        }
      }

      // If any warnings → ask for confirmation
      if (warnMessages.length > 0) {
        const confirmed = window.confirm(
          `Some date fields look incorrect:\n\n${warnMessages.join(
            "\n"
          )}\n\nAre you sure you want to verify anyway?`
        );
        if (!confirmed) return;
      }

      // --- DEFAULT VERIFICATION FLOW ---

      const response = await api.post("/approve-po", { filename, user });

      if (response.status === 200) {
        Toaster.success("PO verified!");
      } else {
        Toaster.error(`Failed to verify: ${response.data.error}`);
      }

    } catch (err) {
      console.error("Error in verifying PO:", err);
      Toaster.error("Error in verifying PO");
    }
  };

  const handleMarkAsInvalid = async () => {
    const confirmed = window.confirm("Are you sure this is NOT a valid PO?");

    if (!confirmed) return;

    try {
      const response = await api.post("/mark-as-invalid", { filename, user });

      if (response.status === 200) {
        Toaster.success("PO marked as invalid!");
        navigate("/po-home"); // Or refresh the page, as needed
      } else {
        Toaster.error(`Failed to mark as invalid: ${response.data.error}`);
      }
    } catch (err) {
      console.error("Error marking PO as invalid:", err);
      Toaster.error("Error marking PO as invalid.");
    }
  };

  const copyFileToUploads = async (customer) => {
    try {
      const response = await api.post("/copy-file-to-uploads", { customer, filename });
      console.log("COPY CALLED for", filename);

      if (response.status !== 200) {
        console.error("Failed to copy file to uploads:", response.data);
        Toaster.error("Failed to copy file to uploads:", response.data);
      }
    } catch (err) {
      console.error("Error calling copy-file-to-uploads:", err);
      Toaster.error("Error calling copy-file-to-uploads:", err);
    }
  };

  const handleAddItemRow = () => {

    setPoData((prevPoData) => {
      const newRow = {};
      const existingKeys = Object.keys((prevPoData["Table Body"]?.[0]) || {});
      existingKeys.forEach((key) => {
        newRow[key] = "";
      });

      const updatedTableBody = [...(prevPoData["Table Body"] || []), newRow];

      return {
        ...prevPoData,
        "Table Body": updatedTableBody,
      };
    });
  };

  const handleSave = async () => {
    if (newSynonym.trim()) {
      // Update local state with new synonym for the current label
      setLabelSynonyms((prev) => {
        const existing = prev[currentEditLabel] || [];
        return {
          ...prev,
          [currentEditLabel]: [
            ...new Set([...existing, newSynonym.trim()]), // Ensure no duplicates
          ],
        };
      });

      // Prepare the data to send to the API
      const correctedHeaders = {
        [currentEditLabel]: [...(labelSynonyms[currentEditLabel] || []), newSynonym.trim()],
      };

      try {
        const response = await api.post("/update-prompt-headers", { correctedHeaders, user});

        if (response.status === 200) {
          Toaster.success("Label reference updated successfully!");
        } else {
          Toaster.error(`Failed to update label reference: ${response.data.error}`);
        }
      } catch (error) {
        console.error("Error updating label reference:", error);
        Toaster.error("Failed to update label reference.");
      }
    }

    setModalOpen(false);
    setNewSynonym("");
  }

  if (loading) return <div className="loading-overlay">
    <div className="loading-spinner">
      <LoadingSpinner />
    </div>
  </div>;
  if (!poData) return <p>No data found</p>;

  const tabLabels = ["Header & Summary", "Items List"];
  const tabContents = [
    <Box className="ag-theme-quartz ag-theme-idbg">
      <AgGridReact
        columnDefs={headerSummaryColumns}
        rowData={headerSummaryRowData}
        onCellValueChanged={onCellValueChanged}
        domLayout="autoHeight"
      />
    </Box>,
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <ContainedButton onClick={handleAddItemRow} startIcon={<AddIcon />}>
          Add Row
        </ContainedButton>
      </Box>
      <Box
        className="ag-theme-quartz ag-theme-idbg"
        sx={{ width: "100%", boxShadow: 1 }}
      >
        <AgGridReact
          columnDefs={itemListColumns}
          rowData={poData["Table Body"]}
          onCellValueChanged={onCellValueChanged}
          domLayout="autoHeight"
          context={{ filename, setPoData, skuMaster, poData }} // add skuMaster
        />
      </Box>
    </>
  ];

  return (
    <div className="ag-table-container"
      style={{ marginLeft: open ? "240px" : "0px" }}>
      
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", mb: 2 }}>
        {/* Left side buttons */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ fontSize: 12, color: "black", borderColor: "#707070" }}
            onClick={handleBackClick}
          >
            Back
          </Button>
          <ContainedButton
            onClick={handleApprovePO}
            disabled={isApproveDisabled}
            title={isApproveDisabled ? "Cannot approve: Outlet Code is missing" : ""}
          >
            Verify
          </ContainedButton>
        </Box>

        {/* Mark as Invalid on the right */}
        <Button
          variant="outlined"
          size="small"
          sx={{ fontSize: 12, color: "red", borderColor: "#d32f2f" }}
          onClick={handleMarkAsInvalid}
        >
          Mark as Invalid
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 3, width: "100%" }}>
        <Box sx={{ width: "50%" }}>
          <CustomTabs
            tabs={tabLabels}
            contents={tabContents}
            value={tabValue}
            setValue={setTabValue}
          />
        </Box>

        <Box sx={{ width: "50%" }}>
          <div className="document-viewer" style={{ width: "100%" }}>
            
            {/* Image Viewer */}
            {isImageFile(filename) && (
              <div 
                style={{
                  height: "750px",          // same as PDF viewer
                  overflow: "auto",
                  border: "1px solid #ccc",
                  borderRadius: "0.5rem",
                  background: "#fafafa"
                }}
              >
                <img
                  src={`/uploads/${filename}`}
                  alt="Uploaded"
                  style={{
                    width: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}

            {/* Excel Viewer */}
            {!isImageFile(filename) && isExcelFile(filename) && excelData && (
              <div
                dangerouslySetInnerHTML={{ __html: excelHtml }}
                style={{
                  height: "750px",
                  overflow: "auto",
                  border: "1px solid #ccc",
                  padding: "12px",
                  borderRadius: "8px",
                  background: "white"
                }}
              />
            )}

            {/* JSON Viewer */}
            {!isImageFile(filename) && isJSONfile(filename) && excelHtml && (
              <div
                dangerouslySetInnerHTML={{ __html: excelHtml }}
                style={{
                  height: "750px",
                  overflow: "auto",
                  border: "1px solid #ccc",
                  padding: "12px",
                  borderRadius: "8px",
                  background: "white"
                }}
              />
            )}

            {/* PDF Viewer (fallback) */}
            {!isImageFile(filename) && !isExcelFile(filename) && !isJSONfile(filename) && (
              <iframe
                src={`/uploads/${filename}`}
                width="100%"
                height="750px"
                style={{ border: "none", borderRadius: "0.5rem" }}
                title="PO Document Viewer"
              />
            )}
          </div>
        </Box>

      </Box>
      {modalOpen && (
        <EditLabelModal
          isModalOpen={modalOpen}
          setIsModalOpen={setModalOpen}
          currentEditLabel={currentEditLabel}
          newSynonym={newSynonym}
          setNewSynonym={setNewSynonym}
          handleSave={handleSave}
        />
      )}
    </div>
  );
};

export default PODetails; 